import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import type { PacketSummary } from '../files/pcapParse';
import {
  buildStreamConversation,
  formatStreamSegmentHex,
  formatStreamText,
  type StreamConversation,
  type StreamSegment,
} from '../files/pcapStream';
import { bindModalDismiss, createModalShell, refreshModalTitle } from './modalDialogShell';

export type StreamDisplayMode = 'text' | 'hex';

export function openPcapStreamDialog(packets: PacketSummary[], streamId: number): void {
  const conversation = buildStreamConversation(packets, streamId);

  const shell = createModalShell({
    titleId: 'pcap-stream-dialog-title',
    helpTopic: dialogHelpTopic('pcapStream'),
    className: 'file-preview-pcap-stream-dialog',
  });

  shell.body.innerHTML = `
    <div class="file-preview-pcap-stream-dialog__toolbar">
      <label>
        <span data-i18n="files.preview.pcap.stream.showAs"></span>
        <select id="pcap-stream-display-mode">
          <option value="text" data-i18n="files.preview.pcap.stream.showAsText"></option>
          <option value="hex" data-i18n="files.preview.pcap.stream.showAsHex"></option>
        </select>
      </label>
      <div class="file-preview-pcap-stream-legend" id="pcap-stream-legend"></div>
    </div>
    <div class="file-preview-pcap-stream-dialog__body" id="pcap-stream-dialog-body"></div>
  `;

  const doneButton = document.createElement('button');
  doneButton.type = 'button';
  doneButton.className = 'button primary';
  doneButton.id = 'pcap-stream-dialog-done';
  doneButton.setAttribute('data-i18n', 'common.close');
  shell.actions.append(doneButton);

  document.body.append(shell.backdrop);

  const modeSelect = shell.body.querySelector('#pcap-stream-display-mode') as HTMLSelectElement;
  const legendEl = shell.body.querySelector('#pcap-stream-legend') as HTMLElement;
  const bodyEl = shell.body.querySelector('#pcap-stream-dialog-body') as HTMLElement;

  let mode: StreamDisplayMode = 'text';

  const refreshLocale = () => {
    refreshModalTitle(shell, {
      title: t('files.preview.pcap.stream.conversationTitle', { id: streamId }),
    });
    applyI18n(shell.dialog);
    renderLegend(legendEl, conversation);
    renderBody(bodyEl, conversation, mode);
  };

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  modeSelect.addEventListener('change', () => {
    mode = modeSelect.value === 'hex' ? 'hex' : 'text';
    renderBody(bodyEl, conversation, mode);
  });

  doneButton.addEventListener('click', close);

  const unbindLocale = bindLocaleRefresh(refreshLocale);
  refreshLocale();
  modeSelect.focus();
}

function renderLegend(legendEl: HTMLElement, conversation: StreamConversation): void {
  legendEl.replaceChildren();

  const a = document.createElement('span');
  a.className = 'file-preview-pcap-stream-legend__item file-preview-pcap-stream-segment--a';
  a.textContent = conversation.endpointA
    ? t('files.preview.pcap.stream.legendA', { endpoint: conversation.endpointA })
    : t('files.preview.pcap.stream.legendA', { endpoint: '—' });

  const b = document.createElement('span');
  b.className = 'file-preview-pcap-stream-legend__item file-preview-pcap-stream-segment--b';
  b.textContent = conversation.endpointB
    ? t('files.preview.pcap.stream.legendB', { endpoint: conversation.endpointB })
    : t('files.preview.pcap.stream.legendB', { endpoint: '—' });

  legendEl.append(a, b);
}

function renderBody(
  bodyEl: HTMLElement,
  conversation: StreamConversation,
  mode: StreamDisplayMode,
): void {
  bodyEl.replaceChildren();

  if (conversation.segments.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.setAttribute('data-i18n', 'files.preview.pcap.stream.empty');
    empty.textContent = t('files.preview.pcap.stream.empty');
    bodyEl.append(empty);
    return;
  }

  for (const segment of conversation.segments) {
    bodyEl.append(renderSegment(segment, mode));
  }

  if (conversation.truncated) {
    const note = document.createElement('p');
    note.className = 'muted file-preview-pcap-stream-truncated';
    note.setAttribute('data-i18n', 'files.preview.pcap.stream.truncated');
    note.textContent = t('files.preview.pcap.stream.truncated');
    bodyEl.append(note);
  }
}

function renderSegment(segment: StreamSegment, mode: StreamDisplayMode): HTMLElement {
  const block = document.createElement('pre');
  block.className = `file-preview-pcap-stream-segment file-preview-pcap-stream-segment--${segment.direction}`;
  block.dataset.direction = segment.direction;
  block.dataset.packetIndex = String(segment.packetIndex);
  block.textContent = mode === 'hex'
    ? formatStreamSegmentHex(segment.bytes)
    : formatStreamText(segment.bytes);
  return block;
}
